import { createStore } from "vuex";

import axios from "axios";

import { resetStoreAfterLogout } from "./store/index";

import auth from "../modules/auth/store";
import calendar from "../modules/calendar/store";
import children from "../modules/children/store";
import childrenGroups from "../modules/childrenGroups/store";
import classrooms from "../modules/classrooms/store";
import dashboard from "../modules/dashboard/store";
import diets from "../modules/diets/store";
import institutions from "../modules/institutions/store";
import meals from "../modules/meals/store";
import administrators from "../modules/administrators/store";
import institutionsWorkers from "../modules/institutionsWorkers/store";
import institutionsWorkersGroups from "../modules/institutionsWorkersGroups/store";
import mealsMenus from "../modules/mealsMenus/store";
import mealsMenusFiles from "../modules/mealsMenusFiles/store";
import mealTypes from "../modules/mealTypes/store";
import orders from "../modules/orders/store";
import parents from "../modules/parents/store";
import reports from "../modules/reports/store";
import settings from "../modules/settings/store";
import terminal from "../modules/terminal/store";
import transactions from "../modules/transactions/store";
import priceLists from "../modules/priceLists/store";
import inventory from "../modules/inventory/store";
import scannedCardEntries from "../modules/scannedCardEntries/store";
import helpTickets from "../modules/helpTickets/store";
import users from "../modules/users/store";
import cooks from "../modules/cooks/store";
import institutionsAdmins from "../modules/institutionsAdmins/store";
import invoicesFiles from "../modules/invoicesFiles/store";
import announcements from "../modules/announcements/store";
import recipients from "../modules/recipients/store";
import kitchens from "../modules/kitchens/store";
import allergens from "../modules/allergens/store";
import portionSizes from "../modules/portionSizes/store";
import tickets from "../modules/tickets/store";
import messages from "../modules/messages/store";
import notifications from "../modules/notifications/store";
import returnRequests from "../modules/returnRequests/store";
import routeNumbers from "../modules/routeNumbers/store";
import dailyMealsReleases from "../modules/dailyMealsReleases/store";
import kitchensMealsReleases from "../modules/kitchensMealsReleases/store";
import { APP_ORIGIN } from "../config";
import districts from "../modules/districts/store";
import mealTypesCategories from "../modules/mealTypesCategories/store";
import ingredientCategories from "../modules/inventory/modules/ingredientCategories/store";
import drivers from "../modules/drivers/store";
import automatedReports from "../modules/automatedReports/store";
import onlinePayments from "../modules/onlinePayments/store";
import ordersImport from "../modules/ordersImport/store";
import emailDeliveryTrackings from "../modules/emailDeliveryTrackings/store.js";
import externalUsers from "../modules/externalUsers/store";
import asyncImport from "../modules/asyncImport/store";
import recipes from "../modules/recipes/store";

const BOOTSTRAP_BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
};

const store = createStore({
  strict: true, // TODO: Disable this on production

  plugins: [resetStoreAfterLogout],

  modules: {
    auth,
    calendar,
    children,
    childrenGroups,
    classrooms,
    dashboard,
    diets,
    mealsMenusFiles,
    institutions,
    meals,
    administrators,
    institutionsWorkers,
    institutionsWorkersGroups,
    mealsMenus,
    mealTypes,
    orders,
    parents,
    reports,
    settings,
    terminal,
    transactions,
    priceLists,
    inventory,
    scannedCardEntries,
    helpTickets,
    users,
    cooks,
    institutionsAdmins,
    invoicesFiles,
    announcements,
    recipients,
    kitchens,
    allergens,
    portionSizes,
    tickets,
    messages,
    notifications,
    returnRequests,
    routeNumbers,
    dailyMealsReleases,
    kitchensMealsReleases,
    districts,
    mealTypesCategories,
    ingredientCategories,
    drivers,
    automatedReports,
    onlinePayments,
    ordersImport,
    emailDeliveryTrackings,
    externalUsers,
    asyncImport,
    recipes,
  },

  state: {
    mobileMenuOpen: false,
    isMobile: false,
    viewportWidth: 0,
    currentBuildVersion: import.meta.env.VITE_APP_VERSION ?? null,
    availableBuildVersion: null,
    hasUnsavedChanges: false,
  },

  getters: {
    mobileMenuOpen: (state) => state.mobileMenuOpen,

    isViewportSmallerOrEqual: (state) => (breakpoint) => {
      return state.viewportWidth <= BOOTSTRAP_BREAKPOINTS[breakpoint];
    },

    currentBuildVersion: (state) => state.currentBuildVersion,
    availableBuildVersion: (state) => state.availableBuildVersion,

    hasUnsavedChanges: (state) => state.hasUnsavedChanges,
  },

  mutations: {
    setMobileMenuOpen(state, value) {
      state.mobileMenuOpen = value;
    },

    toggleSidebar(state) {
      state.mobileMenuOpen = !state.mobileMenuOpen;
    },

    updateViewportSize(state, value) {
      state.viewportWidth = value;
    },

    setCurrentBuildVersion(state, value) {
      state.currentBuildVersion = value;
    },

    setAvailableBuildVersion(state, value) {
      state.availableBuildVersion = value;
    },

    setHasUnsavedChanges(state, value) {
      state.hasUnsavedChanges = value;
    },
  },

  actions: {
    async getBuildVersion({ commit }) {
      let version = null;

      if (import.meta.env.DEV) {
        version = "dev";
      } else {
        const res = await axios.get(`${APP_ORIGIN}/deployment.json`);

        if (!res || typeof res.data !== "object" || !res.data.version) {
          throw new Error("Error occurred while processing your request.");
        }

        version = res.data.version;
      }

      commit("setAvailableBuildVersion", version);

      return version;
    },

    setHasUnsavedChanges({ commit }, value) {
      commit("setHasUnsavedChanges", value);
    },
  },
});

export default store;
